import { inject } from '@angular/core';
import { HttpResourceOptions, HttpResourceRef, HttpResourceRequest, httpResource } from '@angular/common/http';

import { ENVIRONMENT } from '@hmax-order/frontend/environment';

/**
 * Requisição da API com o caminho relativo no lugar da URL absoluta.
 *
 * Query params vão em `params`, nunca concatenados no caminho — é o que o
 * `HttpResourceRequest` espera e o que mantém a chave de cache consistente.
 */
export type ApiRequest = Omit<HttpResourceRequest, 'url'> & {
  /** Caminho relativo à `apiUrl`, sem barra inicial. */
  path: string;
};

/**
 * Recurso de leitura da API, com a base de `ENVIRONMENT.apiUrl` já resolvida.
 *
 * É o substituto do antigo `StateBaseService`. A diferença que importa: aquele
 * cacheava para sempre — uma vez carregado, o dado só saía no logout, então
 * mudança feita no admin nunca chegava na tela. Este refaz o fetch quando a
 * requisição muda e quando a página recarrega.
 *
 * Dado de servidor não vira store. Filtro, ordenação e agregação são `computed`
 * em cima do recurso, não estado copiado.
 *
 * @param request função reativa devolvendo o caminho relativo ou uma
 * {@link ApiRequest}. Devolver `undefined` deixa o recurso ocioso — é assim que
 * se espera por um parâmetro que ainda não existe.
 *
 * @example
 * ```ts
 * readonly cfgs = apiResource<Cfg[]>(() => 'cfgs', { defaultValue: [] });
 *
 * readonly produtos = apiResource<Product[]>(
 *   () => ({ path: 'products', params: { includePrices: 1 } }),
 *   { defaultValue: [] },
 * );
 *
 * readonly produto = apiResource<Product>(() => {
 *   const id = this.produtoId();
 *   return id ? `products/${id}` : undefined;   // ocioso até ter id
 * });
 * ```
 */
export function apiResource<T>(
  request: () => string | ApiRequest | undefined,
  options: HttpResourceOptions<T, unknown> & { defaultValue: NoInfer<T> },
): HttpResourceRef<T>;
export function apiResource<T>(
  request: () => string | ApiRequest | undefined,
  options?: HttpResourceOptions<T, unknown>,
): HttpResourceRef<T | undefined>;
export function apiResource<T>(
  request: () => string | ApiRequest | undefined,
  options?: HttpResourceOptions<T, unknown>,
): HttpResourceRef<T | undefined> {
  const environment = inject(ENVIRONMENT);

  return httpResource<T>(() => {
    const value = request();

    if (value === undefined) {
      return undefined;
    }

    if (typeof value === 'string') {
      return { url: `${environment.apiUrl}/${value}` };
    }

    const { path, ...rest } = value;
    return { ...rest, url: `${environment.apiUrl}/${path}` };
  }, options);
}
